/**
 * Jobs — the list.
 *
 * A table on a desk, cards on a phone. Same data, same order, no second
 * component tree to keep in step: the table is hidden below the breakpoint and
 * the cards above it.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { marginTone, money, percent, titleCase } from "../lib/format";
import { Card, Chip, DataTable, Empty, ErrorState, Loading, PageHeader, type Tone } from "../components/ui";
import type { JobOut, JobStatus } from "../lib/types";

const STATUS_TONE: Partial<Record<JobStatus, Tone>> = {
  on_site: "info",
  in_progress: "info",
  awaiting_parts: "warn",
  awaiting_approval: "warn",
  completed: "ok",
  invoiced: "ok",
  cancelled: "neutral",
};

const FILTERS: { key: string; label: string; match: (j: JobOut) => boolean }[] = [
  { key: "open", label: "Open", match: (j) => j.status !== "invoiced" && j.status !== "cancelled" },
  { key: "today", label: "Scheduled", match: (j) => Boolean(j.scheduled_start) },
  { key: "risk", label: "Losing money", match: (j) => j.revenue_cents > 0 && j.margin_bp < 3100 },
  { key: "all", label: "Everything", match: () => true },
];

export default function Jobs() {
  const { can } = useAuth();
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.jobs.list(),
  });

  const rows = useMemo(() => {
    if (!data) return [];
    const match = FILTERS.find((f) => f.key === filter)?.match ?? (() => true);
    const needle = search.trim().toLowerCase();
    return data.filter(
      (job) =>
        match(job) &&
        (needle === "" ||
          job.title.toLowerCase().includes(needle) ||
          job.number.toLowerCase().includes(needle)),
    );
  }, [data, filter, search]);

  if (isPending) return <Loading what="Loading jobs" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="page">
      <PageHeader
        title="Jobs"
        actions={
          can(...OFFICE) && (
            <Link className="btn btn-primary" to="/jobs/new">
              New job
            </Link>
          )
        }
      >
        Every job hangs off a site asset, so a camera's history outlives the invoice.
      </PageHeader>

      <div className="toolbar">
        <div className="segmented">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? "active" : ""}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="search">
          <input
            type="search"
            value={search}
            placeholder="Job number or title"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {rows.length === 0 ? (
        <Card>
          <Empty title="No jobs match">
            {data.length === 0
              ? "Nothing has been created yet. Accepting a quote creates jobs automatically."
              : "Try a different filter or clear the search."}
          </Empty>
        </Card>
      ) : (
        <>
          <Card padded={false}>
            <div className="desk-only">
              <DataTable
                columns={[
                  { key: "no", label: "Job" },
                  { key: "title", label: "Work" },
                  { key: "type", label: "Type" },
                  { key: "status", label: "Status" },
                  { key: "value", label: "Value", align: "right" },
                  { key: "margin", label: "Margin", align: "right" },
                ]}
              >
                {rows.map((job) => (
                  <tr key={job.id} className="clickable">
                    <td className="mono">
                      <Link className="row-link" to={`/jobs/${job.id}`}>
                        {job.number}
                      </Link>
                    </td>
                    <td>{job.title}</td>
                    <td>
                      <Chip tone="neutral">{titleCase(job.type)}</Chip>
                    </td>
                    <td>
                      <Chip tone={STATUS_TONE[job.status] ?? "neutral"}>{titleCase(job.status)}</Chip>
                    </td>
                    <td className="right mono">{money(job.quoted_cents || job.revenue_cents)}</td>
                    <td className={`right mono text-${marginTone(job.margin_bp)}`}>
                      {job.revenue_cents > 0 ? percent(job.margin_bp) : "—"}
                    </td>
                  </tr>
                ))}
              </DataTable>
            </div>
          </Card>

          <div className="phone-only stack">
            {rows.map((job) => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="jobcard">
                <div className="jobcard-top">
                  <span className="mono jobcard-no">{job.number}</span>
                  <Chip tone={STATUS_TONE[job.status] ?? "neutral"}>{titleCase(job.status)}</Chip>
                </div>
                <span className="jobcard-title">{job.title}</span>
                <div className="jobcard-foot">
                  <span>{titleCase(job.type)}</span>
                  <span className="mono">{money(job.quoted_cents || job.revenue_cents)}</span>
                  {job.revenue_cents > 0 && (
                    <span className={`mono text-${marginTone(job.margin_bp)}`}>{percent(job.margin_bp)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
